/**
 * Hold the references to the data. Provide functions to access the data and filters.
 *
 * @model app/components/SportsDataRepository
 */
import _ from 'lodash';
import jQuery from 'jquery';
import { isClientFeatureEnabled } from '@wh/feature-toggle-detector';

import { get } from '../../utils';


/**
 * Stores updates to the model which relate to content not yet
 * present on the page. This may grow big...
 * If more than one update comes, for the same id and property
 * then only the last received value is queued.
 *
 * @type {Object}
 */
var modelQueue = {};

/**
 * Accepted field updates to queue. This is a big improvement since
 * we don't care at all about updates to order, or other similar fields.
 */
var queueModelAcceptedFields = [
    'displayed',
    'status',
    'price',
];

/**
 * Model
 *
 * @type {Object}
 */
var model = {
    selections: {},
    markets: {},
    events: {},
    competitions: {},
};

/**
 * Hierarchy
 *
 * @type {Object}
 */
var hierarchy = {
    selections: 'markets',
    markets: 'events',
    events: 'competitions',
};

/**
 * Mapping of entity ID prefixes to types.
 *
 * @type {Object}
 */
var types = {
    'OB_OU': 'selections',
    'OB_MA': 'markets',
    'OB_EV': 'events',
};

/**
 * Get node type if node ID is defined
 *
 * @param {String} nodeId
 *
 * @returns {String}
 *
 * @private
 */
var getDefinedNodeType = function (nodeId) {

    var nodeType = nodeId.substr(0, 5);
    return types.hasOwnProperty(nodeType) ? types[nodeType] : '';
};

/**
 * Get the type of the node from the id.
 *
 * @param {String} nodeId
 *
 * @returns {String}
 *
 * @private
 */
var getNodeType = function (nodeId) {

    if (typeof nodeId === 'undefined') {
        return '';
    }

    return getDefinedNodeType(nodeId);
};

/**
 * Gets a queued node. Also inits a new position if a given node has not been queued before
 *
 * @param {Object} node
 *
 * @returns {Object} queued object
 *
 * @private
 */
var createQueueNode = function (node) {

    if (!modelQueue.hasOwnProperty(node.id)) {

        return modelQueue[node.id] = { id: node.id };
    } else {
        return modelQueue[node.id];
    }
};

/**
 * Queues model updates for lazy loaded data. If a model is being lazy loaded we need to queue the changes
 * received from push and try to apply those same changes once we got the data
 *
 * @param {Object} node
 *
 * @returns {Object|Boolean} queued object or false
 *
 * @private
 */
var queueModelUpdate = function (node) {

    if (!modelQueue.hasOwnProperty(node.id) && queueModelAcceptedFields.indexOf(node.attribute) === -1) {

        return false;
    }

    const model = createQueueNode(node);
    model[node.attribute] = node.value;

    return model;
};

/**
 * Update the model with the node attributes
 *
 * @param {Object} node Node
 * @param {String} nodeType Type of node
 *
 * @returns {Mixed}
 *
 * @private
 */
var updateNodeInternals = function (node, nodeType) {

    // we might not have the node which triggered the update on the model
    if (!model[nodeType][node.id]) {

        queueModelUpdate(node);
        return false;
    }

    model[nodeType][node.id][node.attribute] = node.value;

    api.setDomElement(node, nodeType);

    return model[nodeType][node.id];
};

/**
 * Update the model with the node attributes
 *
 * @param {Object} node
 *
 * @returns {Mixed}
 *
 * @private
 */
var updateNodeValue = function (node) {

    return updateNodeInternals(node, getNodeType(node.id));
};

/**
 * Validate the attributes of a node
 *
 * @param {Object} node
 *
 * @returns {Boolean}
 *
 * @private
 */
var validateNode = function (node) {

    return (node.hasOwnProperty('id') &&
        node.hasOwnProperty('attribute') &&
        node.hasOwnProperty('value'));
};

/**
 * Update the parent node reference with the current node inserted
 *
 * @param {Object} nodeI
 * @param {Object} nodeType
 * @param {String} parentId
 * @param {String} parentType
 *
 * @returns {boolean}
 *
 * @private
 */
var updateParentReference = function (nodeId, nodeType, parentId, parentType) {

    //Add the parent ref. to the current node
    var parentNames = {
        selections: 'selections',
        markets: 'market',
        events: 'event',
        competitions: 'competition',
    };
    model[nodeType][nodeId][parentNames[parentType] + 'Id'] = parentId;

    //Add the node's ref. to the parent.
    if (model[parentType].hasOwnProperty(parentId) && model[parentType][parentId][nodeType]) {
        model[parentType][parentId][nodeType].push(nodeId);
    }

    return true;
};

/**
 * Creates a selection property initialized to an
 * empty array if none exists for the given node.
 *
 * @param {Object} node to be processed
 *
 * @returns {void}
 *
 * @private
 */
var initSelectionsArray = function (node) {

    if (!node.selections) {
        node.selections = [];
    }
};

/**
 * Gets an entity from the model or the model queue
 *
 * @param {string} type of the entity
 * @param {string} entityId entity id
 *
 * @returns {Object}
 *
 * @private
 */
var getNodeFromModelOrQueue = function (type, entityId) {

    return model[type][entityId] || modelQueue[entityId];
};

/**
 * Gets a node entity. THis function is useful in cases where we actually have an id
 * instead of the actual entity object
 *
 * @param {Object} node
 * @param {string} type
 *
 * @returns {Object}
 *
 * @private
 */
var getNodeEntity = function (node, type) {

    if (_.isObject(node)) {

        return node;
    }

    return getNodeFromModelOrQueue(type, node);
};

/**
 * function updates index for type and node.
 */
function updateIndex(type, node) {
    if (model[type] && model[type][node.id]) {
        if (model[type][node.id].$dom) {
            model[type][node.id].$dom.push(node);
        } else {
            model[type][node.id].$dom = jQuery([node]);
        }
    }
}
/**
 * function returns selector to events nodes
 *
 * @Returns string
 */
function getIndexModelSelector(prefix) {
    return `[data-entityid^="${prefix}"]:not([data-odds=SP])`;
}

/**
 * function performs scan of html code passed as argument, if no argument provided performs full HTML scan for event,
 * market and selection nodes to aggregate $dom references in the model.
 */
function indexDom(html) {
    for (let [prefix, type] of Object.entries(types)) {
        const selector = getIndexModelSelector(prefix);

        jQuery(selector, html).each((index, node) => {
            updateIndex(type, node);
        });

        for (let node of Object.values(model[type] || {})) {
            if (!node.hasOwnProperty('$dom')) {
                node.$dom = jQuery([]);
            }
        }
    }
}

/**
 * function clears dom index
 */
function clearDomIndex() {
    for (let type of Object.values(types)) {
        const nodes = Object.keys(model[type] || {});

        nodes.forEach((id) => {
            model[type][id].$dom = jQuery([]);
        });
    }
}

/**
 * Checks if the experimental diffusion optimisation is enabled through a feature flag
 *
 * @returns {boolean}
 */
function isOptimisationEnabled() {
    const featureQuery = get(window, 'whStatic.config.diffusion.queryFeatureToggleOptimisationEnabled', null);
    const isFeatureEnabled = get(window, 'whStatic.config.diffusion.optimisationEnabled', false);

    if (isFeatureEnabled) return true;
    if (!featureQuery) return false;
    return isClientFeatureEnabled(featureQuery);
}

const optimisationEnabled = isOptimisationEnabled();

var api = {

    /**
     * Sets the whole model at once based on the passed json
     *
     * @param {Object} jsonData
     *
     * @returns {void}
     *
     * @public
     */
    setModel: function (jsonData) {

        model.selections = jsonData.selections;
        model.markets = jsonData.markets;
        model.events = jsonData.events;
        model.competitions = jsonData.competitions;
    },

    /**
     * Adds a node to the model. Add the parent reference if provided.
     *
     * @param {Object} node
     * @param {String} nodeType
     * @param {String} parentId
     *
     * @returns {*}
     *
     * @public
     */
    addNode: function (node, nodeType, parentId) {

        var nodeEntity = getNodeEntity(node, nodeType);

        model[nodeType][nodeEntity.id] = nodeEntity;
        model[nodeType][nodeEntity.id][hierarchy[nodeType]] = [];

        if (typeof parentId !== 'undefined') {
            updateParentReference(nodeEntity.id, nodeType, parentId, hierarchy[nodeType]);
        }

        return model[nodeType][nodeEntity.id];
    },

    /**
     * Get Model
     *
     * @returns {Object}
     *
     * @public
     */
    getModel: function () {

        return model;
    },

    /**
     * Returns an object in the model queue which stores updates received to elements
     * which are not yet added to the page
     *
     * @param  {string} id entity id to get
     *
     * @return {object || undefined}    the object containing the updated properties for this object or undefined
     *
     * @private
     */
    getEntityFromQueue: function (id) {

        return modelQueue[id];
    },

    /**
     * Update one of the nodes and refresh the model in case of status/display updates.
     *
     * @param {Object} node
     *
     * @returns {Boolean}
     *
     * @public
     */
    update: function (node) {

        return validateNode(node) && updateNodeValue(node);
    },

    /**
     * Adds a selection to the model
     *
     * @param {string} marketId parent market id
     * @param {Object} entity selection to be added
     *
     * @returns {Object} updated node
     *
     * @public
     */
    addSelectionToModel: function (marketId, entity) {

        var node = getNodeFromModelOrQueue('markets', marketId);

        if (!node) {
            node = createQueueNode(marketId);
        }

        initSelectionsArray(node);

        node.selections.push(entity);

        return node;
    },

    /**
     * Moves an entity from the queue to the model after fetching all its data
     *
     * @param  {string} type   type of the entity to be added. Possible values are:
     *                         selections/markets/events/competitions
     * @param  {Object} entity object to be added to the model
     *
     * @return {void}
     *
     * @private
     */
    queueToModel: function (type, entity) {

        model[type][entity.id] = _.extend({}, entity);

        delete modelQueue[entity.id];

        entity = null;
    },

    /**
     * Nullifies the queue of non existing entities and resets the model
     *
     * @return {void}
     *
     * @public
     */
    clearQueue: function () {

        modelQueue = {};
    },
    /**
     * Sets the DOM Element
     *
     * @param {Object} node The node we are setting the dom for
     * @param {String} nodeType A type from : event, market, selection
     *
     * @returns {void}
     *
     * @public
     */
    setDomElement: optimisationEnabled ? () => {} : function setDomElement(node, nodeType) {
        if (!(model[nodeType][node.id].hasOwnProperty('$dom') &&
          model[nodeType][node.id].$dom.length > 0)) {
            model[nodeType][node.id].$dom = jQuery('[data-entityid="' + node.id + '"]:not([data-odds=SP])');
        }
    },
    indexDom: optimisationEnabled ? indexDom : () => {},
    clearDomIndex: optimisationEnabled ? clearDomIndex : () => {},
};

export default api;
